import type { DrivingLimitStatus } from '../../../api/apiService';

// ALL-136 parts 1–2 (design). These shapes are what the UI expects; the Dev
// maps the backend responses onto them. All durations are in seconds and all
// instants are ISO 8601 strings, like DrivingLimitStatus.
//
// Decision (Ronald, 2026-10-30): no pause in the app, and all three limits
// count driving time only (time in «A conduzir»). The app does not measure
// legal working time.

/**
 * The driver's state, the same three as the app today.
 * - resting: no open session («Sessão inativa»).
 * - available: session open, not driving («Sessão ativa»). Not counted.
 * - driving: session open and driving («A conduzir»). Counted in every limit.
 */
export type DriverWorkState = 'resting' | 'available' | 'driving';

export interface DriverWorkStateSnapshot {
  state: DriverWorkState;
  /** When the current state started; drives the on-screen timer. */
  since: string;
}

/** Driving time this calendar week, Monday 00:00 to Sunday 24:00 (local time). */
export interface WeeklyDrivingLimit {
  driven_seconds: number;
  limit_seconds: number;
  week_start: string;
  week_end: string;
  calculated_at: string;
}

/**
 * Average weekly driving time over a rolling 17-week window (about 4 months),
 * this week included. Decision (CEO, 2026-10-31): rolling 17 weeks, not fixed
 * calendar periods.
 */
export interface FourMonthAverageLimit {
  average_weekly_seconds: number;
  limit_weekly_seconds: number;
  /** Weeks in the rolling window: 17. */
  window_weeks: number;
  /**
   * Total driving this week (Monday to Sunday) that keeps the 17-week average
   * at or below the limit: limit × 17 − driving in the other 16 weeks of the
   * window. The «até X h esta semana» hint; at or below this week's driving
   * when the average can no longer stay at the limit this week.
   */
  allowed_this_week_seconds: number;
  /** Monday 00:00 of the oldest week in the window. */
  window_start: string;
  /** End of the current week (next Monday 00:00). */
  window_end: string;
  /** False while the driver has less than 17 weeks of records. */
  has_full_history: boolean;
  calculated_at: string;
}

export interface DriverLimits {
  /** The existing rolling 24 h driving status (ALL-41), unchanged. */
  rolling24h: DrivingLimitStatus | null;
  week: WeeklyDrivingLimit | null;
  fourMonth: FourMonthAverageLimit | null;
}

/**
 * Visual level of one limit.
 * Week and 4-month average: warning from 80 %, critical from 95 %.
 * Rolling 24 h: follows the server status as today (warning = 1 h before the
 * limit by default), so the existing 10 h warning keeps its behaviour.
 */
export type LimitLevel = 'ok' | 'warning' | 'critical' | 'reached' | 'unknown';

export type LimitKey = 'rolling24h' | 'week' | 'fourMonth';
